// The Replays list's URL search ⇄ the region's GET /replays filters. The URL keeps human units
// (seconds, a range preset) so links like People → person → replays are readable and shareable.
import { z } from "zod";
import { REPLAY_ORDERS, REPLAY_PLATFORMS, type ReplayListParams } from "../api/schemas";

export const REPLAY_RANGES = [
  { value: "-24h", label: "Last 24 hours", hours: 24 },
  { value: "-7d", label: "Last 7 days", hours: 7 * 24 },
  { value: "-30d", label: "Last 30 days", hours: 30 * 24 },
  { value: "-90d", label: "Last 90 days", hours: 90 * 24 },
] as const;
export type ReplayRange = (typeof REPLAY_RANGES)[number]["value"];

export const PAGE_SIZE = 25;

export const ReplaySearch = z.object({
  /** A distinct id, or a person UUID (from People). */
  person: z.string().trim().min(1).max(200).optional(),
  min_duration_s: z.coerce.number().int().min(0).max(4 * 3600).optional(),
  errors: z.boolean().optional(),
  rage: z.boolean().optional(),
  platform: z.enum(REPLAY_PLATFORMS).optional(),
  app_version: z.string().trim().min(1).max(64).optional(),
  event: z.string().trim().min(1).max(200).optional(),
  range: z.enum(REPLAY_RANGES.map((r) => r.value) as [ReplayRange, ...ReplayRange[]]).optional(),
  order: z.enum(REPLAY_ORDERS).optional(),
  page: z.coerce.number().int().min(0).max(4000).optional(),
});
export type ReplaySearch = z.infer<typeof ReplaySearch>;

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export function toListParams(s: ReplaySearch, now: Date = new Date()): ReplayListParams {
  const range = REPLAY_RANGES.find((r) => r.value === (s.range ?? "-30d")) ?? REPLAY_RANGES[2];
  const p: ReplayListParams = {
    date_from: new Date(now.getTime() - range.hours * 3_600_000).toISOString(),
    date_to: now.toISOString(),
    order: s.order ?? "start_desc",
    limit: PAGE_SIZE,
    offset: (s.page ?? 0) * PAGE_SIZE,
  };
  if (s.person) {
    if (UUID_RE.test(s.person)) p.person_id = s.person;
    else p.distinct_id = s.person;
  }
  if (s.min_duration_s) p.min_duration_ms = s.min_duration_s * 1000;
  if (s.errors) p.has_errors = true;
  if (s.rage) p.min_rage_clicks = 1;
  if (s.platform) p.platform = s.platform;
  if (s.app_version) p.app_version = s.app_version;
  if (s.event) p.event = s.event;
  return p;
}

/** How many filters narrow the list (for the "Clear N filters" affordance). */
export const activeFilterCount = (s: ReplaySearch): number =>
  [s.person, s.min_duration_s, s.errors, s.rage, s.platform, s.app_version, s.event].filter((v) => v !== undefined && v !== false).length;

/** "4m 05s" / "1h 02m" / "38s". */
export function formatDurationMs(ms: number): string {
  const s = Math.max(0, Math.round(ms / 1000));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  if (h) return `${h}h ${String(m).padStart(2, "0")}m`;
  if (m) return `${m}m ${String(sec).padStart(2, "0")}s`;
  return `${sec}s`;
}

export const PLATFORM_LABEL: Record<(typeof REPLAY_PLATFORMS)[number], string> = {
  web: "Web",
  ios: "iOS",
  android: "Android",
  "react-native": "React Native",
  tauri: "Desktop",
};
